import React from 'react';
import { getColor, getCycleColor } from './colorUtils';
import type { ColorSet } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TimelinePhase {
  label: string;
  duration?: string;
  description?: string;
  milestones?: string[];
  status?: 'completed' | 'active' | 'upcoming';
  color?: string;
}

interface PhasedTimelineProps {
  title?: string;
  phases: TimelinePhase[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve color for a phase. Status takes precedence over the explicit color
 * prop; if neither is provided, fall back to the cycling palette.
 */
function resolveColor(phase: TimelinePhase, index: number): ColorSet {
  if (phase.status === 'completed') return getColor('emerald');
  if (phase.status === 'active') return getColor('accent');
  if (phase.status === 'upcoming') return getColor('gray');
  if (phase.color) return getColor(phase.color);
  return getCycleColor(index);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Marker circle sitting on the timeline line. */
const PhaseMarker: React.FC<{ phase: TimelinePhase; colorSet: ColorSet }> = ({
  phase,
  colorSet,
}) => {
  const isActive = phase.status === 'active';

  return (
    <div className="relative flex items-center justify-center">
      {/* Pulse ring for active status */}
      {isActive && (
        <span
          className={`absolute w-8 h-8 rounded-full ${colorSet.bg} opacity-20 animate-ping`}
          aria-hidden="true"
        />
      )}
      <span
        className={`relative z-10 w-4 h-4 rounded-full ${colorSet.bg} border-2 border-white shadow`}
      />
    </div>
  );
};

/** Card displaying phase details. */
const PhaseCard: React.FC<{
  phase: TimelinePhase;
  colorSet: ColorSet;
}> = ({ phase, colorSet }) => {
  return (
    <div
      className={`rounded-lg border ${colorSet.light} border-l-4 ${colorSet.border} p-3 w-full`}
    >
      {/* Label */}
      <p className="font-semibold text-sm text-gray-900 m-0 leading-tight">
        {phase.label}
      </p>

      {/* Duration badge */}
      {phase.duration && (
        <span className="inline-block mt-1 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
          {phase.duration}
        </span>
      )}

      {/* Description */}
      {phase.description && (
        <p className="text-sm text-gray-600 mt-1.5 mb-0 leading-snug">
          {phase.description}
        </p>
      )}

      {/* Milestones */}
      {phase.milestones && phase.milestones.length > 0 && (
        <ul className="mt-2 mb-0 pl-3.5 list-disc space-y-0.5">
          {phase.milestones.map((ms, i) => (
            <li key={i} className="text-xs text-gray-500 leading-snug">
              {ms}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desktop horizontal layout
// ---------------------------------------------------------------------------

const HorizontalTimeline: React.FC<{
  phases: TimelinePhase[];
  colors: ColorSet[];
}> = ({ phases, colors }) => {
  return (
    <div className="hidden md:block">
      {/* Phase cards row */}
      <div className="flex">
        {phases.map((phase, index) => (
          <div key={index} className="flex-1 px-2">
            <PhaseCard phase={phase} colorSet={colors[index]} />
          </div>
        ))}
      </div>

      {/* Timeline line + markers */}
      <div className="relative flex items-center mt-4">
        {/* Horizontal line spanning full width */}
        <div className="absolute left-0 right-0 h-0.5 bg-gray-300" />

        {/* Markers positioned at center of each phase column */}
        <div className="relative flex w-full">
          {phases.map((phase, index) => (
            <div key={index} className="flex-1 flex justify-center">
              <PhaseMarker phase={phase} colorSet={colors[index]} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mobile vertical layout
// ---------------------------------------------------------------------------

const VerticalTimeline: React.FC<{
  phases: TimelinePhase[];
  colors: ColorSet[];
}> = ({ phases, colors }) => {
  return (
    <div className="md:hidden relative flex">
      {/* Vertical line on the left */}
      <div className="absolute left-[7px] top-0 bottom-0 w-0.5 bg-gray-300" />

      {/* Phase entries */}
      <div className="flex flex-col gap-4 w-full">
        {phases.map((phase, index) => (
          <div key={index} className="flex items-start gap-3">
            {/* Marker */}
            <div className="relative flex-shrink-0 mt-3">
              <PhaseMarker phase={phase} colorSet={colors[index]} />
            </div>

            {/* Card */}
            <div className="flex-1 min-w-0">
              <PhaseCard phase={phase} colorSet={colors[index]} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PhasedTimeline: React.FC<PhasedTimelineProps> = ({ title, phases }) => {
  if (!phases || phases.length === 0) {
    return null;
  }

  const resolvedColors: ColorSet[] = phases.map((phase, i) =>
    resolveColor(phase, i),
  );

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Timeline content */}
      <div className="p-5 md:p-6">
        <HorizontalTimeline phases={phases} colors={resolvedColors} />
        <VerticalTimeline phases={phases} colors={resolvedColors} />
      </div>
    </div>
  );
};

export default PhasedTimeline;
